import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';

/**
 * Customer-facing storefront appearance.
 *
 * The merchant owns the default (`light` | `dark` | `system`) and whether a
 * visitor may override it. A visitor override is only `light` or `dark` and
 * is saved per store, never globally across merchants.
 *
 * Why a Context (not just a hook with local state): there are several
 * consumers — the layout root reads `resolvedTheme` to set the
 * `data-storefront-theme` attribute and compute dark-mode style
 * overrides; the header toggle reads it to flip its sun/moon icon and
 * calls `toggle()` to mutate it. If each consumer ran its own
 * `useState`, a click on the toggle would only re-render the toggle —
 * the layout's data attribute would stay stale until a page refresh.
 * Lifting state into a provider makes the click instant everywhere.
 *
 * Why localStorage and not the URL: customers who are allowed to choose an
 * appearance should retain it when they reload or navigate within the same
 * merchant's storefront. The key includes the store ID so Store A's choice
 * cannot affect Store B.
 *
 * Important: this is storefront-only. The merchant dashboard has no
 * dark mode (yet) and intentionally doesn't read this key.
 */

const VALID_DEFAULT_MODES = ['light', 'dark', 'system'];
const VALID_VISITOR_MODES = ['light', 'dark'];

const normalizeDefaultMode = (value) => (
  VALID_DEFAULT_MODES.includes(value) ? value : 'light'
);

const storageKeyFor = (storeKey) => (
  storeKey ? `sf-theme:${storeKey}` : null
);

const readVisitorPreference = (storeKey) => {
  if (typeof window === 'undefined') return null;
  const key = storageKeyFor(storeKey);
  if (!key) return null;
  try {
    const value = window.localStorage.getItem(key);
    return VALID_VISITOR_MODES.includes(value) ? value : null;
  } catch {
    return null;
  }
};

const writeVisitorPreference = (storeKey, value) => {
  const key = storageKeyFor(storeKey);
  if (!key) return;
  try {
    window.localStorage.setItem(key, value);
  } catch {
    /* private mode / quota exceeded — swallow */
  }
};

const getSystemTheme = () => {
  if (typeof window === 'undefined' || !window.matchMedia) return 'light';
  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
};

const StorefrontThemeContext = createContext(null);

export const StorefrontThemeProvider = ({
  children,
  storeKey,
  defaultMode = 'light',
  allowVisitorOverride = false,
}) => {
  const safeDefaultMode = normalizeDefaultMode(defaultMode);
  const canToggle = allowVisitorOverride === true;
  const [visitorPreference, setVisitorPreference] = useState(() => (
    canToggle ? readVisitorPreference(storeKey) : null
  ));
  const [systemTheme, setSystemTheme] = useState(getSystemTheme);

  // A store may change while this provider remains mounted, and merchants can
  // turn the switcher on/off from their dashboard. Re-read only the scoped
  // visitor choice; a hidden switcher intentionally ignores it.
  useEffect(() => {
    setVisitorPreference(canToggle ? readVisitorPreference(storeKey) : null);
  }, [storeKey, canToggle]);

  // Track the OS preference so a merchant-selected `system` default remains
  // live. Keeping the state fresh also makes an eventual switch to `system`
  // instantaneous.
  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return undefined;
    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    const handler = (e) => setSystemTheme(e.matches ? 'dark' : 'light');
    if (mq.addEventListener) mq.addEventListener('change', handler);
    else mq.addListener(handler); // Safari < 14
    return () => {
      if (mq.removeEventListener) mq.removeEventListener('change', handler);
      else mq.removeListener(handler);
    };
  }, []);

  const setPreference = useCallback((next) => {
    if (!canToggle || !VALID_VISITOR_MODES.includes(next)) return;
    setVisitorPreference(next);
    writeVisitorPreference(storeKey, next);
  }, [canToggle, storeKey]);

  const merchantTheme = safeDefaultMode === 'system' ? systemTheme : safeDefaultMode;
  const resolvedTheme = canToggle && visitorPreference ? visitorPreference : merchantTheme;

  // Toggle flips light/dark and creates a scoped visitor override. There is no
  // visitor-facing `system` state; that remains a merchant default policy.
  const toggle = useCallback(() => {
    if (!canToggle) return;
    setPreference(resolvedTheme === 'dark' ? 'light' : 'dark');
  }, [canToggle, resolvedTheme, setPreference]);

  const value = useMemo(
    () => ({
      resolvedTheme,
      preference: visitorPreference,
      setPreference,
      toggle,
      canToggle,
      defaultMode: safeDefaultMode,
    }),
    [resolvedTheme, visitorPreference, setPreference, toggle, canToggle, safeDefaultMode]
  );

  return (
    <StorefrontThemeContext.Provider value={value}>
      {children}
    </StorefrontThemeContext.Provider>
  );
};

/**
 * Returns `{ resolvedTheme, preference, setPreference, toggle, canToggle }`.
 *
 * When called outside `<StorefrontThemeProvider>` (which shouldn't
 * happen in normal storefront use) the hook falls back to a stable
 * read-from-localStorage object — keeps it safe for any incidental
 * import without leaving the page in a broken state. The fallback
 * deliberately can't mutate shared state, so a stray callsite won't
 * silently drift out of sync.
 */
export const useStorefrontTheme = () => {
  const ctx = useContext(StorefrontThemeContext);
  if (ctx) return ctx;

  // Fallback only — see comment above.
  return {
    resolvedTheme: 'light',
    preference: null,
    setPreference: () => {},
    toggle: () => {},
    canToggle: false,
    defaultMode: 'light',
  };
};

export default useStorefrontTheme;
